import { SearchParamsOption } from 'ky';
import { LngLatBounds } from 'mapbox-gl';

import { OrdersFiltersResponse } from '../types/filters';
import {
  CreateOrderDto,
  OrderDetailsResponse,
  OrdersListResponse,
  OrdersValuesFilters,
  OrdersValuesResponse,
} from '../types/orders';
import { PratiquesResponse } from '../types/pratiques';
import {
  CarbonPlanDebugDetails,
  CarbonPlanDebugDto,
  DensityPlanDebugDetails,
  DensityPlanDebugDto,
  SamplingPlanDetailsResponse,
  SamplingPlanDto,
  SamplingPlansListResponse,
  SamplingPlansMapsResponse,
  SamplingPlansValuesFilters,
  SamplingPlansValuesResponse,
} from '../types/sampling';

import { saveAs } from 'file-saver';

import {
  Filters,
  FORM_STATUS,
  MultisectActionDto,
  formatFilters,
  handleRequest,
  privateClient,
} from '@data-access';

export const samplingApi = {
  plans: {
    list: async (filters: Filters) => {
      const res = await privateClient
        .get('sampling_plans/', {
          searchParams: {
            ...formatFilters(filters),
          },
        })
        .json<SamplingPlansListResponse>();

      return res;
    },
    getById: async (id: number) => {
      const res = await privateClient
        .get(`sampling_plans/${id}/`)
        .json<SamplingPlanDetailsResponse>();

      return res;
    },
    getValues: async (filters: SamplingPlansValuesFilters) => {
      const filteredParams = Object.fromEntries(
        Object.entries(filters).filter(
          ([_, v]) => v !== undefined && v !== null && v !== '',
        ),
      );
      const res = await privateClient
        .extend({ timeout: 20000 })
        .get('sampling_plans/values/', {
          searchParams: filteredParams,
        })
        .json<SamplingPlansValuesResponse>();

      return res;
    },
    getMaps: async (filters: Filters) => {
      const res = await privateClient
        .get('sampling_plan_maps/', {
          searchParams: {
            ...formatFilters(filters),
          },
        })
        .json<SamplingPlansMapsResponse>();

      return res;
    },
    create: async (data: SamplingPlanDto) => {
      const res = await privateClient
        .post('sampling_plans/', {
          json: data,
        })
        .json<unknown>();

      return res;
    },
    deleteMany: async (data: MultisectActionDto) => {
      const res = await privateClient
        .delete('sampling_plans/bulk_delete/', {
          json: data,
        })
        .json<unknown>();

      return res;
    },
    exportSynthesis: async (data: MultisectActionDto) => {
      const res = await handleRequest<{
        id: number;
        status: FORM_STATUS;
        instance_id: number;
      }>(
        privateClient
          .post(`sampling_plans/prep_synthesis/`, {
            json: data,
          })
          .json(),
      );

      return res;
    },
    downloadExport: async (opts: { id: number }) => {
      const res = await handleRequest(
        privateClient.post(`sampling_plans/synthesis/`, {
          json: {
            instance_id: opts.id,
          },
        }),
      );

      // Extract filename from header
      const filename = res.headers
        .get('content-disposition')
        ?.split(';')
        .find((n) => n.includes('filename='))
        ?.replace('filename=', '')
        .trim();

      const blob = await res.blob();

      saveAs(blob, filename);

      return res;
    },
    exportShp: async (data: MultisectActionDto) => {
      const res = await handleRequest(
        privateClient.post(`sampling_plans/export_shp/`, {
          json: data,
        }),
      );

      const filename = res.headers
        .get('content-disposition')
        ?.split(';')
        .find((n) => n.includes('filename='))
        ?.replace('filename=', '')
        .trim();

      const blob = await res.blob();

      saveAs(blob, filename);

      return res;
    },
    carbon: {
      getDebugDetails: async (id: number) => {
        const res = await privateClient
          .get(`debug_panel/carbon/${id}/`)
          .json<CarbonPlanDebugDetails>();

        return res;
      },
      update: async (args: { id: number; data: CarbonPlanDebugDto }) => {
        const res = await privateClient
          .patch(`debug_panel/carbon/${args.id}/`, {
            json: args.data,
          })
          .json<CarbonPlanDebugDetails>();

        return res;
      },
    },
    density: {
      getDebugDetails: async (id: number) => {
        const res = await privateClient
          .get(`debug_panel/density/${id}/`)
          .json<DensityPlanDebugDetails>();

        return res;
      },
      update: async (args: { id: number; data: DensityPlanDebugDto }) => {
        const res = await privateClient
          .patch(`debug_panel/density/${args.id}/`, {
            json: args.data,
          })
          .json<DensityPlanDebugDetails>();

        return res;
      },
    },
  },

  orders: {
    list: async (filters: Filters) => {
      const res = await privateClient
        .get('commands/', {
          searchParams: {
            ...formatFilters(filters),
          },
        })
        .json<OrdersListResponse>();

      return res;
    },
    getById: async (id: number) => {
      const res = await privateClient
        .get(`commands/${id}/`)
        .json<OrderDetailsResponse>();

      return res;
    },
    getFilters: async () => {
      const res = await privateClient
        .get('commands/filters/')
        .json<OrdersFiltersResponse>();

      return res;
    },
    getValues: async (filters: OrdersValuesFilters) => {
      const filteredParams = Object.fromEntries(
        Object.entries(filters).filter(
          ([_, v]) => v !== undefined && v !== null && v !== '',
        ),
      );
      const res = await privateClient
        .get('commands/values/', {
          searchParams: filteredParams,
        })
        .json<OrdersValuesResponse>();

      return res;
    },
    create: async (data: CreateOrderDto) => {
      const res = await privateClient
        .post('commands/', {
          json: data,
        })
        .json<unknown>();

      return res;
    },
    updatePoints: async (args: {
      commandId: number;
      data: { ids: string[]; cancel: boolean; add_in_new_command: boolean };
    }) => {
      const res = await privateClient
        .post(`commands/${args.commandId}/update_points/`, {
          json: args.data,
        })
        .json<unknown>();

      return res;
    },
  },
  pratiques: {
    list: async (filters: Filters) => {
      const res = await privateClient
        .get('pratiques/', {
          searchParams: {
            ...formatFilters(filters),
          },
        })
        .json<PratiquesResponse>();

      return res;
    },
  },
};
